import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Popconfirm, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ALIMTALK_SEND_HISTORY_DETAIL_URL_PATH } from '../../../../../../constants/service/common/urlConstants';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../../../../../constants/service/common/userPermissions';
import { useAuth } from '../../../../../../hooks/common/useAuth';
import AlimtalkSendStatusTag from '../AlimtalkSendStatusTag';
import { AlimtalkSendHistoryItem } from '../../types';
import { formatCount, formatDateTime, formatSendType } from '../../utils';
import { toDetailLocationState } from '../../listSearchParams';

interface Props {
  items: AlimtalkSendHistoryItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onChangePage: (page: number, pageSize: number) => void;
  /** Popconfirm 이 완료까지 확인 버튼 로딩을 유지하도록 Promise 를 반환한다 */
  onCancel: (id: number) => Promise<unknown>;
  /** 진행 중인 취소 요청의 예약 ID (없으면 null) — 진행 중에는 모든 취소 버튼을 막아 중복 요청을 방지 */
  cancelPendingId: number | null;
}

const buildDetailPath = (id: number): string => ALIMTALK_SEND_HISTORY_DETAIL_URL_PATH.replace(':id', String(id));

/**
 *  처리/대상 셀 — 두 수치를 비율(진행률)로 계산하지 않고 숫자만 나란히 보여준다.
 *  스펙(「발송 결과 데이터 규칙」)상 targetCount 는 DB 의 중복 제거 대상 수, processedCount 는 summary 처리 수로
 *  산출 기준이 달라 비율이 진행률을 뜻하지 않는다. 집계가 없으면 0 으로 단정하지 않고 '-' 로 표기한다.
 */
const ProcessedTargetCell = ({
  processed,
  target,
}: {
  processed: number | null;
  target: number | null;
}): JSX.Element => {
  if (processed === null && target === null) {
    return <Typography.Text type="secondary">-</Typography.Text>;
  }

  return (
    <Typography.Text>
      처리 {formatCount(processed)} / 대상 {formatCount(target)}
    </Typography.Text>
  );
};

const AlimtalkSendHistoryTable = ({
  items,
  loading,
  page,
  pageSize,
  totalCount,
  onChangePage,
  onCancel,
  cancelPendingId,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { can } = useAuth();
  // 백엔드: 상세 GET 은 read, 예약 취소 DELETE /alimtalk-sends/:id 는 update 권한을 요구한다.
  const canRead = can(PERMISSION_ACTIONS.READ, PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE);
  const canCancel = can(PERMISSION_ACTIONS.UPDATE, PERMISSION_SUBJECTS.ALIMTALK_SEND_SCHEDULE);
  const isCancelPending = cancelPendingId !== null;

  const columns: ColumnsType<AlimtalkSendHistoryItem> = [
    {
      title: '상세보기',
      key: 'detail',
      fixed: 'left',
      width: 88,
      render: (_, record) =>
        canRead ? (
          <Button
            size="small"
            onClick={() => navigate(buildDetailPath(record.id), { state: toDetailLocationState(search) })}
          >
            상세보기
          </Button>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    { title: '예약ID', dataIndex: 'id', key: 'id', width: 90 },
    {
      title: '예약일시',
      dataIndex: 'scheduledAt',
      key: 'scheduledAt',
      width: 170,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: '발송유형',
      dataIndex: 'sendType',
      key: 'sendType',
      width: 130,
      render: (_, record) => formatSendType(record.sendType),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (_, record) => <AlimtalkSendStatusTag status={record.status} isTest={record.isTest} />,
    },
    {
      title: '대상모수',
      dataIndex: 'targetCount',
      key: 'targetCount',
      width: 90,
      align: 'right',
      render: (value: number | null) => formatCount(value),
    },
    {
      title: '성공',
      dataIndex: 'successCount',
      key: 'successCount',
      width: 80,
      align: 'right',
      render: (value: number | null) => formatCount(value),
    },
    {
      title: '실패',
      dataIndex: 'failedCount',
      key: 'failedCount',
      width: 80,
      align: 'right',
      render: (value: number | null) => formatCount(value),
    },
    {
      title: '처리/대상',
      key: 'processedTarget',
      width: 170,
      render: (_, record) => <ProcessedTargetCell processed={record.processedCount} target={record.targetCount} />,
    },
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      width: 220,
      render: (value: string) =>
        value ? (
          <Tooltip title={value}>
            <Typography.Text ellipsis style={{ maxWidth: 200 }}>
              {value}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: '등록자',
      key: 'admin',
      width: 120,
      render: (_, record) => record.admin?.name ?? '-',
    },
    {
      title: '관리',
      key: 'action',
      fixed: 'right',
      width: 96,
      render: (_, record) =>
        record.canCancel && canCancel ? (
          <Popconfirm
            title="예약 취소"
            description="해당 예약을 취소하시겠습니까?"
            okText="취소 요청"
            cancelText="닫기"
            okButtonProps={{ danger: true, loading: cancelPendingId === record.id }}
            onConfirm={() => onCancel(record.id)}
            disabled={isCancelPending}
          >
            <Button size="small" danger disabled={isCancelPending}>
              취소
            </Button>
          </Popconfirm>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
  ];

  return (
    <Table<AlimtalkSendHistoryItem>
      rowKey="id"
      size="middle"
      loading={loading}
      columns={columns}
      dataSource={items}
      scroll={{ x: 1500 }}
      pagination={{
        current: page,
        pageSize,
        total: totalCount,
        showSizeChanger: true,
        pageSizeOptions: [20, 50, 100],
        showTotal: (total, range) => (
          <Space size={4}>
            <Typography.Text>
              총 {total.toLocaleString()}건 중 {range[0].toLocaleString()}-{range[1].toLocaleString()}
            </Typography.Text>
          </Space>
        ),
        onChange: onChangePage,
      }}
    />
  );
};

export default AlimtalkSendHistoryTable;
